/**
 * repeat-to.directive.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Directive, Input, TemplateRef, ViewContainerRef } from "@angular/core";

/**
 * The context class allows sub-views to access the "index" parameter, which
 * will carry the entry's position in the list.
 */
class RepeatToContext {
    constructor (public index : number) {}
}

/**
 * The maximum number of "reasonable" sub-views, to prevent an accidental
 * assignment of a huge number from overloading the view parser.
 */
const MAX_REASONABLE_COUNT : number = 500; // TODO: return to 100

/**
 * The `*repeatTo` directive is a succinct way to iterate over an ad-hoc list
 * or repeat elements a set number of times *without* an already existing
 * array structure. It's helpful when you'd otherwise need to go to the extent
 * of creating an new array just to do an *ngFor iterator.
 *
 * The context defines an index variable available for assignment. For example:
 *  `<div *repeatTo="4; let i = index">{{i}}</div>`
 * which will generate:
 *  `<div>0</div><div>1</div><div>2</div><div>3</div>`
 */
@Directive({ selector: "[repeatTo]"})
export class RepeatToDirective {

    /**
     * The current "length" of the repeatTo. Used to determine how changes must
     * be applied when the value is updated.
     */
    public count : number = 0;

    constructor (
        private templateRef : TemplateRef<any>,
        private viewContainer : ViewContainerRef
    ) {/* DI */}

    /**
     * Apply the directive and generate the specified number of subviews.
     */
    @Input() set repeatTo (newCount : number)
    {
        if (!Number.isInteger(newCount) || newCount < 0)
        {
            // If the parameter is negative or not a number, zero it out.
            newCount = 0;
        }
        if (newCount === this.count)
        {
            return;
        }
        this._applyChanges(newCount);
    }

    /**
     * Add or remove subviews to achieve the necessary number of repeated items.
     * @param newCount The update count of views
     */
    private _applyChanges (newCount : number) : void
    {
        if (newCount > MAX_REASONABLE_COUNT)
        {
            // safeguard against absurdity
            throw new Error(
                `Requested unreasonable number of sub-views (${newCount})`);
        }
        while (this.count < newCount)
        {
            this.viewContainer.createEmbeddedView(
                this.templateRef,
                new RepeatToContext(this.count),
                this.count
            );
            this.count++;
        }
        while (this.count > newCount)
        {
            this.viewContainer.remove();
            this.count--;
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/utilities/repeat-to.directive.ts